<template>
  <div class="p-2 sm:p-4 text-lexend overflow-x-hidden min-h-screen bg-gray-50 dark:bg-gray-900/50">
    <!-- Header -->
    <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
      <div>
        <div class="flex items-center gap-2 mb-1">
          <div class="w-8 h-1 bg-violet-600 rounded-full"></div>
          <span class="text-[10px] sm:text-xs font-black text-violet-600 tracking-widest">Trazabilidad de Pagos</span>
        </div>
        <h1 class="text-xl sm:text-3xl font-black text-gray-900 dark:text-white tracking-tight flex items-center gap-2">
          ¿Dónde cayó la plata?
          <span class="px-3 py-0.5 bg-violet-100 dark:bg-violet-900/40 text-violet-600 dark:text-violet-300 text-xs sm:text-sm font-black rounded-lg">{{ filteredPagos.length }}</span>
        </h1>
      </div>
      <div class="flex flex-wrap gap-2">
        <button @click="exportarPDF" class="px-4 py-2 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-xl font-black text-xs flex items-center gap-2 shadow-sm">
          <Icon icon="mdi:file-pdf-box" class="text-lg" /> PDF
        </button>
        <button @click="loadPagos" class="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-xl font-black text-xs flex items-center gap-2">
          <Icon icon="mdi:refresh" :class="{'animate-spin': loading}" /> Actualizar
        </button>
        <button @click="openCreateModal" class="px-5 py-2 bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white rounded-xl font-black text-xs shadow-md flex items-center gap-2">
          <Icon icon="mdi:plus-circle" class="text-lg" /> Registrar Pago
        </button>
      </div>
    </div>

    <!-- BARRA DE TOTALES -->
    <div class="bg-gradient-to-r from-violet-600 to-fuchsia-600 rounded-2xl p-4 mb-4 shadow-md flex flex-col sm:flex-row justify-between items-center gap-4 text-white">
      <div class="flex items-center gap-3">
        <div class="p-2 bg-white/20 rounded-xl">
          <Icon icon="mdi:calculator" class="text-2xl" />
        </div>
        <div>
          <p class="text-[10px] font-black uppercase tracking-widest text-white/80">Total Filtrado</p>
          <p class="text-xs font-bold text-white/90">Basado en los filtros actuales</p>
        </div>
      </div>
      <div class="flex gap-6 text-right">
        <div>
          <p class="text-[10px] font-black text-white/70 uppercase">Monto USD</p>
          <p class="text-2xl font-black">{{ formatearMonto(totalUsd, 'USD') }}</p>
        </div>
        <div>
          <p class="text-[10px] font-black text-white/70 uppercase">Monto Bs</p>
          <p class="text-xl font-black mt-1">{{ formatearMonto(totalBs, 'VES') }}</p>
        </div>
      </div>
    </div>

    <!-- Filtros -->
    <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 mb-4">
      <div class="grid grid-cols-1 sm:grid-cols-4 gap-3">
        <div>
          <label class="block text-[10px] font-black text-gray-400 mb-2">Tipo Documento</label>
          <select v-model="filterTipoDoc" class="w-full px-3 py-2.5 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 focus:border-violet-500 rounded-xl font-black text-sm dark:text-white outline-none">
            <option value="all">TODOS</option>
            <option v-for="t in TIPOS_DOCUMENTO_PAGO" :key="t.value" :value="t.value">{{ t.label }}</option>
          </select>
        </div>
        <div>
          <label class="block text-[10px] font-black text-gray-400 mb-2">Método</label>
          <select v-model="filterMetodo" class="w-full px-3 py-2.5 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 focus:border-violet-500 rounded-xl font-black text-sm dark:text-white outline-none">
            <option value="all">TODOS</option>
            <option v-for="m in METODOS_PAGO" :key="m.value" :value="m.value">{{ m.label }}</option>
          </select>
        </div>
        <div>
          <label class="block text-[10px] font-black text-gray-400 mb-2">Desde</label>
          <input type="date" v-model="fechaDesde" class="w-full px-3 py-2.5 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 focus:border-violet-500 rounded-xl text-sm dark:text-white outline-none" />
        </div>
        <div>
          <label class="block text-[10px] font-black text-gray-400 mb-2">Hasta</label>
          <input type="date" v-model="fechaHasta" class="w-full px-3 py-2.5 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 focus:border-violet-500 rounded-xl text-sm dark:text-white outline-none" />
        </div>
      </div>
      <div class="relative mt-3">
        <Icon icon="mdi:magnify" class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xl" />
        <input v-model="searchQuery" placeholder="Referencia, código operación PM, titular, cuenta..." class="w-full pl-10 pr-3 py-2.5 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 focus:border-violet-500 rounded-xl outline-none dark:text-white font-bold text-sm" />
      </div>
    </div>

    <!-- Lista de pagos AGRUPADOS por Cuenta/Titular -->
    <div v-if="loading" class="space-y-2">
      <div v-for="i in 5" :key="i" class="h-24 bg-white dark:bg-gray-800 rounded-2xl animate-pulse"></div>
    </div>
    <div v-else-if="filteredPagos.length === 0" class="bg-white dark:bg-gray-800 rounded-2xl p-16 text-center">
      <Icon icon="mdi:cash-off" class="text-6xl text-gray-300 mx-auto mb-3" />
      <p class="text-lg font-black text-gray-400">Sin pagos registrados</p>
    </div>
    
    <div v-else class="space-y-6">
      <!-- Iteramos sobre los grupos de cuentas -->
      <div v-for="grupo in pagosAgrupados" :key="grupo.numeroCuenta" class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        
        <!-- Cabecera del Grupo (Banco, Cuenta y Titular) -->
        <div class="bg-blue-50 dark:bg-blue-900/20 px-4 py-3 border-b border-blue-100 dark:border-blue-800 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
          <div class="flex items-center gap-3">
            <div class="w-10 h-10 bg-blue-600 text-white rounded-xl flex items-center justify-center">
              <Icon icon="mdi:bank" class="text-xl" />
            </div>
            <div>
              <p class="text-xs font-black text-gray-900 dark:text-white">{{ grupo.bancoNombre }} ({{ grupo.bancoCodigo }})</p>
              <p class="font-mono text-[11px] text-blue-700 dark:text-blue-300">{{ formatearNumeroCuenta(grupo.numeroCuenta) }}</p>
            </div>
          </div>
          <div class="sm:text-right">
            <p class="text-[10px] font-black text-blue-500 uppercase">Titular</p>
            <p class="font-black text-gray-900 dark:text-white text-sm">{{ grupo.titularNombreCompleto }}</p>
            <p class="text-[10px] text-gray-600 dark:text-gray-400">{{ grupo.titularDocumento }} · {{ grupo.titularTelefono }}</p>
          </div>
          <div class="sm:text-right bg-white dark:bg-gray-800 px-3 py-1.5 rounded-lg shadow-sm border border-blue-100 dark:border-blue-800">
            <p class="text-[9px] font-black text-blue-500 uppercase">Total en esta cuenta</p>
            <p class="text-sm font-black text-emerald-600">{{ formatearMonto(grupo.totalUsd, 'USD') }}</p>
            <p class="text-[10px] font-bold text-gray-500">{{ formatearMonto(grupo.totalBs, 'VES') }}</p>
          </div>
        </div>

        <!-- Lista de pagos individuales dentro de esta cuenta -->
        <div class="divide-y divide-gray-100 dark:divide-gray-700">
          <div v-for="p in grupo.pagos" :key="p.idPagoCuentaBancaria" class="p-4 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
            <div class="flex items-start justify-between gap-3 flex-wrap">
              <div class="flex items-center gap-3">
                <div :class="metodoBg(p.metodoPago)" class="w-10 h-10 rounded-lg flex items-center justify-center">
                  <Icon :icon="getMetodoPagoIcon(p.metodoPago)" class="text-white text-lg" />
                </div>
                <div>
                  <p class="text-sm font-black text-gray-900 dark:text-white">
                    {{ formatearMetodoPago(p.metodoPago) }}
                    <span class="ml-2 text-[10px] px-2 py-0.5 bg-gray-100 dark:bg-gray-700 rounded-lg text-gray-600 dark:text-gray-300">{{ formatearTipoDocumentoPago(p.tipoDocumentoPago) }}</span>
                  </p>
                  <p class="text-[10px] text-gray-500">Doc #{{ p.idDocumentoPago }} · {{ formatFecha(p.fechaPago) }}</p>
                </div>
              </div>
              <div class="text-right">
                <p class="text-lg font-black text-emerald-600">{{ formatearMonto(p.montoUsd, 'USD') }}</p>
                <p class="text-[10px] text-gray-500">{{ formatearMonto(p.montoBs, 'VES') }}</p>
              </div>
            </div>

            <!-- Referencia del pago -->
            <div class="mt-2 text-xs flex items-center gap-2">
               <span class="font-black text-violet-500 uppercase text-[9px]">Referencia:</span>
               <span class="font-mono text-[11px] text-gray-900 dark:text-white">{{ p.referencia || '—' }}</span>
               <span v-if="p.pmCodigoOperacion" class="ml-2 text-[10px] text-violet-700 dark:text-violet-300">PM: {{ p.pmCodigoOperacion }}</span>
            </div>

            <!-- Pago móvil detalle -->
            <div v-if="p.metodoPago === 'pago_movil' || p.pmCodigoOperacion" class="mt-2 p-2 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg grid grid-cols-2 sm:grid-cols-5 gap-2 text-[10px]">
              <div>
                <span class="font-black text-indigo-500">Banco Emisor:</span>
                <span class="font-bold">{{ nombreBanco(p.pmBancoEmisorCodigo) }} ({{ p.pmBancoEmisorCodigo || '—' }})</span>
              </div>
              <div>
                <span class="font-black text-indigo-500">Banco Receptor:</span>
                <span class="font-bold">{{ nombreBanco(p.pmBancoReceptorCodigo) }} ({{ p.pmBancoReceptorCodigo || '—' }})</span>
              </div>
              <div><span class="font-black text-indigo-500">Tel. Emisor:</span> <span class="font-bold">{{ p.pmTelefonoEmisor || '—' }}</span></div>
              <div><span class="font-black text-indigo-500">Doc. Emisor:</span> <span class="font-bold">{{ p.pmDocumentoEmisor || '—' }}</span></div>
              <div><span class="font-black text-indigo-500">Código Op.:</span> <span class="font-bold">{{ p.pmCodigoOperacion || '—' }}</span></div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- MODAL REGISTRAR PAGO -->
    <Teleport to="body">
      <div v-if="showModal" class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[9998]" @click.self="showModal = false">
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-hidden animate-zoomIn">
          <div class="bg-gradient-to-r from-violet-600 to-fuchsia-600 px-6 py-4 flex items-center gap-3">
            <Icon icon="mdi:cash-plus" class="text-white text-2xl" />
            <div>
              <h3 class="text-sm font-black text-white">Registrar Pago en Cuenta</h3>
              <p class="text-[10px] text-white/70">Con datos de pago móvil si aplica</p>
            </div>
            <button @click="showModal = false" class="ml-auto p-1.5 bg-white/10 rounded-lg"><Icon icon="mdi:close" class="text-white" /></button>
          </div>

          <div class="p-6 overflow-y-auto max-h-[calc(92vh-160px)]">
            <form @submit.prevent="savePago" class="space-y-4">
              <!-- Documento origen -->
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label class="block text-[10px] font-black text-gray-400 mb-2">Tipo Documento *</label>
                  <select v-model="form.tipoDocumentoPago" required class="w-full px-3 py-2.5 bg-white dark:bg-gray-900/50 border-2 border-gray-200 dark:border-gray-600 focus:border-violet-500 rounded-xl text-sm font-black dark:text-white outline-none">
                    <option v-for="t in TIPOS_DOCUMENTO_PAGO" :key="t.value" :value="t.value">{{ t.label }}</option>
                  </select>
                </div>
                <div>
                  <label class="block text-[10px] font-black text-gray-400 mb-2">ID Documento *</label>
                  <input v-model.number="form.idDocumentoPago" type="number" required class="w-full px-3 py-2.5 bg-white dark:bg-gray-900/50 border-2 border-gray-200 dark:border-gray-600 focus:border-violet-500 rounded-xl text-sm font-mono dark:text-white outline-none" />
                </div>
              </div>

              <!-- Cuenta destino -->
              <div>
                <label class="block text-[10px] font-black text-gray-400 mb-2">Cuenta Bancaria *</label>
                <select v-model="form.idCuentaBancaria" required class="w-full px-3 py-2.5 bg-white dark:bg-gray-900/50 border-2 border-gray-200 dark:border-gray-600 focus:border-violet-500 rounded-xl text-sm font-black dark:text-white outline-none">
                  <option :value="null" disabled>Selecciona cuenta</option>
                  <option v-for="c in cuentas" :key="c.idCuentaBancaria" :value="c.idCuentaBancaria">
                    {{ c.bancoNombre }} — {{ c.titularNombreCompleto }} ({{ formatearNumeroCuenta(c.numeroCuenta) }})
                  </option>
                </select>
              </div>

              <!-- Montos -->
              <div class="grid grid-cols-3 gap-3">
                <div>
                  <label class="block text-[10px] font-black text-gray-400 mb-2">Monto USD *</label>
                  <input v-model.number="form.montoUsd" type="number" step="0.01" required class="w-full px-3 py-2.5 bg-white dark:bg-gray-900/50 border-2 border-gray-200 dark:border-gray-600 focus:border-violet-500 rounded-xl text-sm font-black dark:text-white outline-none" />
                </div>
                <div>
                  <label class="block text-[10px] font-black text-gray-400 mb-2">Monto Bs *</label>
                  <input v-model.number="form.montoBs" type="number" step="0.01" required class="w-full px-3 py-2.5 bg-white dark:bg-gray-900/50 border-2 border-gray-200 dark:border-gray-600 focus:border-violet-500 rounded-xl text-sm font-black dark:text-white outline-none" />
                </div>
                <div>
                  <label class="block text-[10px] font-black text-gray-400 mb-2">Tasa Bs/USD</label>
                  <input v-model.number="form.tasaBsUsd" type="number" step="0.01" class="w-full px-3 py-2.5 bg-white dark:bg-gray-900/50 border-2 border-gray-200 dark:border-gray-600 focus:border-violet-500 rounded-xl text-sm dark:text-white outline-none" />
                </div>
              </div>

              <!-- Método + referencia -->
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label class="block text-[10px] font-black text-gray-400 mb-2">Método *</label>
                  <select v-model="form.metodoPago" required class="w-full px-3 py-2.5 bg-white dark:bg-gray-900/50 border-2 border-gray-200 dark:border-gray-600 focus:border-violet-500 rounded-xl text-sm font-black dark:text-white outline-none">
                    <option v-for="m in METODOS_PAGO" :key="m.value" :value="m.value">{{ m.label }}</option>
                  </select>
                </div>
                <div>
                  <label class="block text-[10px] font-black text-gray-400 mb-2">Referencia</label>
                  <input v-model="form.referencia" class="w-full px-3 py-2.5 bg-white dark:bg-gray-900/50 border-2 border-gray-200 dark:border-gray-600 focus:border-violet-500 rounded-xl text-sm dark:text-white outline-none" />
                </div>
              </div>

              <!-- Pago Móvil (condicional) -->
              <div v-if="form.metodoPago === 'pago_movil'" class="bg-indigo-50 dark:bg-indigo-900/20 rounded-xl p-4 border border-indigo-200 dark:border-indigo-800">
                <p class="text-[10px] font-black text-indigo-600 dark:text-indigo-300 mb-3 flex items-center gap-1"><Icon icon="mdi:cellphone" /> DATOS PAGO MÓVIL</p>
                <div class="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <div>
                    <label class="block text-[9px] font-black text-gray-400 mb-1">Banco Emisor</label>
                    <input v-model="form.pmBancoEmisorCodigo" maxlength="4" class="w-full px-2 py-2 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 rounded-lg text-xs font-mono dark:text-white outline-none" placeholder="0102" />
                    <p v-if="form.pmBancoEmisorCodigo" class="text-[9px] text-indigo-500 font-bold mt-0.5 truncate">{{ nombreBanco(form.pmBancoEmisorCodigo) }}</p>
                  </div>
                  <div>
                    <label class="block text-[9px] font-black text-gray-400 mb-1">Banco Receptor</label>
                    <input v-model="form.pmBancoReceptorCodigo" maxlength="4" class="w-full px-2 py-2 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 rounded-lg text-xs font-mono dark:text-white outline-none" placeholder="0134" />
                    <p v-if="form.pmBancoReceptorCodigo" class="text-[9px] text-indigo-500 font-bold mt-0.5 truncate">{{ nombreBanco(form.pmBancoReceptorCodigo) }}</p>
                  </div>
                  <div><label class="block text-[9px] font-black text-gray-400 mb-1">Código Operación</label><input v-model="form.pmCodigoOperacion" maxlength="50" class="w-full px-2 py-2 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 rounded-lg text-xs font-mono dark:text-white outline-none" /></div>
                  <div><label class="block text-[9px] font-black text-gray-400 mb-1">Teléfono Emisor</label><input v-model="form.pmTelefonoEmisor" maxlength="20" class="w-full px-2 py-2 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 rounded-lg text-xs dark:text-white outline-none" /></div>
                  <div><label class="block text-[9px] font-black text-gray-400 mb-1">Documento Emisor</label><input v-model="form.pmDocumentoEmisor" maxlength="30" class="w-full px-2 py-2 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 rounded-lg text-xs dark:text-white outline-none" /></div>
                  <div><label class="block text-[9px] font-black text-gray-400 mb-1">Fecha Pago</label><input v-model="form.fechaPago" type="datetime-local" class="w-full px-2 py-2 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 rounded-lg text-xs dark:text-white outline-none" /></div>
                </div>
              </div>

              <div>
                <label class="block text-[10px] font-black text-gray-400 mb-2">Observaciones</label>
                <textarea v-model="form.observaciones" rows="2" class="w-full px-3 py-2.5 bg-white dark:bg-gray-900/50 border-2 border-gray-200 dark:border-gray-600 focus:border-violet-500 rounded-xl text-sm dark:text-white outline-none resize-none"></textarea>
              </div>

              <div class="flex gap-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                <button type="button" @click="showModal = false" class="flex-1 py-3 border-2 border-gray-200 text-gray-600 rounded-xl font-black text-xs">Cancelar</button>
                <button type="submit" :disabled="saving" class="flex-[2] py-3 bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white rounded-xl font-black text-xs uppercase shadow-lg disabled:opacity-50 flex items-center justify-center gap-2">
                  <Icon :icon="saving ? 'mdi:loading' : 'mdi:content-save'" :class="{'animate-spin': saving}" />
                  {{ saving ? 'Guardando...' : 'Registrar Pago' }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import { Icon } from "@iconify/vue";
import {
  obtenerPagosCuenta, obtenerCuentasBancarias, crearPagoCuenta,
  METODOS_PAGO, TIPOS_DOCUMENTO_PAGO,
  formatearMetodoPago, getMetodoPagoIcon, formatearMonto,
  formatearNumeroCuenta, formatearTipoDocumentoPago
} from '@/api/bancos.js';

// IMPORTAMOS LA FUNCIÓN DEL GENERADOR DE PDF
import { descargarPDFPagos } from '@/utils/reportePagosdebancoGenerator';

const BANCOS_CODIGOS = {
  '0102': 'Banco de Venezuela', '0104': 'Venezolano de Crédito', '0105': 'Mercantil',
  '0108': 'Provincial', '0114': 'Bancaribe', '0115': 'Exterior', '0128': 'Banco Caroní',
  '0134': 'Banesco', '0137': 'Sofitasa', '0138': 'Banco Plaza', '0146': 'Bangente',
  '0151': 'BFC Banco Fondo Común', '0156': '100% Banco', '0157': 'DelSur',
  '0163': 'Banco del Tesoro', '0166': 'Banco Agrícola de Venezuela', '0168': 'Bancrecer',
  '0169': 'Mi Banco', '0171': 'Banco Activo', '0172': 'Bancamiga', '0174': 'Banplus',
  '0175': 'Bicentenario', '0177': 'Banfanb', '0191': 'BNC Banco Nacional de Crédito'
};

export default {
  name: 'PagosCuentaBancaria',
  components: { Icon },
  data() {
    return {
      METODOS_PAGO, TIPOS_DOCUMENTO_PAGO,
      pagos: [], cuentas: [],
      loading: false, saving: false,
      showModal: false,
      searchQuery: '',
      filterTipoDoc: 'all',
      filterMetodo: 'all',
      fechaDesde: '',
      fechaHasta: '',
      form: this.formVacio()
    };
  },
  computed: {
    // 1. Filtramos los pagos según los inputs del usuario
    filteredPagos() {
      let f = [...this.pagos];
      if (this.searchQuery.trim()) {
        const q = this.searchQuery.toLowerCase();
        f = f.filter(p =>
          p.referencia?.toLowerCase().includes(q) ||
          p.pmCodigoOperacion?.toLowerCase().includes(q) ||
          p.titularNombreCompleto?.toLowerCase().includes(q) ||
          p.numeroCuenta?.toLowerCase().includes(q)
        );
      }
      if (this.filterTipoDoc !== 'all') f = f.filter(p => p.tipoDocumentoPago === this.filterTipoDoc);
      if (this.filterMetodo !== 'all') f = f.filter(p => p.metodoPago === this.filterMetodo);
      if (this.fechaDesde) f = f.filter(p => new Date(p.fechaPago) >= new Date(this.fechaDesde));
      if (this.fechaHasta) f = f.filter(p => new Date(p.fechaPago) <= new Date(this.fechaHasta + 'T23:59:59'));
      return f;
    },

    // 2. Calculamos los totales globales basados en los filtros
    totalUsd() {
      return this.filteredPagos.reduce((sum, p) => sum + (Number(p.montoUsd) || 0), 0);
    },
    totalBs() {
      return this.filteredPagos.reduce((sum, p) => sum + (Number(p.montoBs) || 0), 0);
    },

    // 3. Agrupamos los pagos por cuenta para que sea legible y no se confundan titulares
    pagosAgrupados() {
      const grupos = {};

      this.filteredPagos.forEach(pago => {
        const key = pago.numeroCuenta || 'sin_cuenta';
        
        if (!grupos[key]) {
          grupos[key] = {
            numeroCuenta: pago.numeroCuenta,
            bancoNombre: pago.bancoNombre,
            bancoCodigo: pago.bancoCodigo,
            titularNombreCompleto: pago.titularNombreCompleto,
            titularDocumento: pago.titularDocumento,
            titularTelefono: pago.titularTelefono,
            pagos: [],
            totalUsd: 0,
            totalBs: 0
          };
        }

        // Sumamos al grupo
        grupos[key].pagos.push(pago);
        grupos[key].totalUsd += (Number(pago.montoUsd) || 0);
        grupos[key].totalBs += (Number(pago.montoBs) || 0);
      });

      // Devolvemos un array con los grupos (ordenado por fecha del pago más reciente)
      return Object.values(grupos).sort((a, b) => {
        const lastA = new Date(a.pagos[0].fechaPago).getTime();
        const lastB = new Date(b.pagos[0].fechaPago).getTime();
        return lastB - lastA;
      });
    }
  },
  mounted() {
    this.loadPagos();
    this.loadCuentas();
  },
  methods: {
    formVacio() {
      return {
        tipoDocumentoPago: 'venta', idDocumentoPago: null, idCuentaBancaria: null,
        montoUsd: 0, montoBs: 0, tasaBsUsd: 0,
        metodoPago: 'pago_movil', referencia: '',
        fechaPago: new Date().toISOString().slice(0, 16),
        pmBancoEmisorCodigo: '', pmBancoReceptorCodigo: '',
        pmTelefonoEmisor: '', pmDocumentoEmisor: '', pmCodigoOperacion: '',
        observaciones: ''
      };
    },
    formatearMetodoPago, getMetodoPagoIcon, formatearMonto,
    formatearNumeroCuenta, formatearTipoDocumentoPago,
    nombreBanco(codigo) {
      if (!codigo) return '—';
      return BANCOS_CODIGOS[codigo] || codigo;
    },
    metodoBg(metodo) {
      return {
        pago_movil: 'bg-gradient-to-br from-indigo-500 to-purple-600',
        transferencia: 'bg-gradient-to-br from-blue-500 to-cyan-600',
        efectivo: 'bg-gradient-to-br from-emerald-500 to-teal-600',
        zelle: 'bg-gradient-to-br from-purple-500 to-pink-600',
        binance: 'bg-gradient-to-br from-amber-500 to-orange-600',
        paypal: 'bg-gradient-to-br from-blue-600 to-indigo-700'
      }[metodo] || 'bg-gradient-to-br from-gray-500 to-gray-700';
    },
    formatFecha(f) {
      if (!f) return '—';
      try { return new Date(f).toLocaleString('es-VE'); } catch { return f; }
    },
    async loadPagos() {
      this.loading = true;
      try {
        const res = await obtenerPagosCuenta();
        if (res.success) this.pagos = res.data;
      } finally { this.loading = false; }
    },
    async loadCuentas() {
      const res = await obtenerCuentasBancarias();
      if (res.success) this.cuentas = res.data.filter(c => c.estado === 'activo');
    },
    openCreateModal() {
      this.form = this.formVacio();
      this.showModal = true;
    },
    async savePago() {
      this.saving = true;
      try {
        const res = await crearPagoCuenta(this.form);
        if (res.success) {
          await this.loadPagos();
          this.showModal = false;
        }
      } finally { this.saving = false; }
    },
    // MÉTODO PARA EXPORTAR EL PDF
    async exportarPDF() {
      const filtrosAplicados = {
        desde: this.fechaDesde,
        hasta: this.fechaHasta,
        metodo: this.filterMetodo,
      };
      
      // Exportamos exactamente lo que está filtrado en pantalla
      await descargarPDFPagos(
        this.filteredPagos, 
        'REPORTE DE PAGOS AGRUPADOS POR CUENTA', 
        filtrosAplicados
      );
    }
  }
};
</script>

<style scoped>
.text-lexend { font-family: 'Lexend', sans-serif; }
@keyframes zoomIn { from {opacity:0; transform:scale(.95);} to {opacity:1; transform:scale(1);} }
.animate-zoomIn { animation: zoomIn .2s ease-out; }
</style>